<template>
  <div class="container-print">
    <HeaderLeftContainer :title="$t(`views.leftBar.printMap`)" :back-to="{ name: 'default' }"></HeaderLeftContainer>

    <v-container fluid class="d-flex flex-column flex-grow-1">
      <div class="py-4">
        <p class="pt-2">
          {{ $t('views.printMap.description') }}
        </p>
      </div>
      <div class="py-4">
        <v-text-field
          v-model="nameField"
          variant="outlined"
          :label="$t('domain.fields.name')"
          hide-details
          color="rgb(var(--v-theme-lighten))"
          heigth="100px" />
      </div>
      <div class="py-4">
        <v-textarea
          v-model="description"
          :label="$t('domain.fields.description')"
          auto-grow
          variant="outlined"
          rows="5"
          row-height="15"
          hide-details />
      </div>
      <!-- <div class="py-4 d-flex justify-space-between">
        <p class="mt-4">{{ $t("views.printMap.graticule") }}</p>
        <div>
          <v-switch color="rgb(var(--v-theme-background))" hide-details />
        </div>
      </div>
      <div class="py-4 d-flex justify-space-between">
        <p class="mt-4">{{ $t("common.concepts.legend") }}</p>
        <div>
          <v-switch color="rgb(var(--v-theme-background))" hide-details />
        </div>
      </div> -->
    </v-container>
    <v-container fluid class="action-buttons">
      <WidgetWrapper style="width: auto" @ready="ready">
        <v-row class="justify-content-around d-flex justify-space-between">
          <v-col>
            <v-btn class="cancel-button pa-2" block size="medium" variant="flat" @click="closePrintmap">
              {{ $t('common.actions.cancel') }}
            </v-btn>
          </v-col>
          <v-col>
            <v-btn class="pa-2" :loading="printing" block color="rgb(var(--v-theme-primary))" size="medium" variant="flat" @click="print">
              {{ $t('common.actions.print') }}
            </v-btn>
          </v-col>
        </v-row>
      </WidgetWrapper>
    </v-container>
  </div>
</template>

<script lang="ts" setup>
// Framework import
import { ref, shallowRef } from 'vue'

import { useRouter } from 'vue-router'

import type { i18nObject } from '@tsyl/karonte/lib/types/i18nObject'
import type { Layer } from '@tsyl/karonte/lib/types/layers'
import type { WidgetBuilder } from '@tsyl/karonte/lib/types/widgets'
import { WidgetWrapper } from '@tsyl/karonte-widgets'

// Components import
import HeaderLeftContainer from '@/components/HeaderLeftContainer.vue'

const router = useRouter()

const nameField = ref('' as string)
const printing = ref(false)
const description = ref('' as string)
const widgetBuilderRef = shallowRef<WidgetBuilder | undefined>()

const ready = async (widgetBuilder: WidgetBuilder) => {
  await widgetBuilder.waitReady()
  widgetBuilderRef.value = widgetBuilder
}

function closePrintmap() {
  router.push({ name: 'default' })
}

function localizedText(text: string | i18nObject | undefined) {
  if (typeof text !== 'object') return text
  return text[window.navigator.language] || text['en-US']
}

function buildLegend() {
  // `layers` is protected in the karonte view wrapper, but the legend needs the layers currently on the map
  const view = widgetBuilderRef.value?.view as unknown as { layers?: Layer[] } | undefined
  const layers = view?.layers
  if (!layers) return []
  return layers.flatMap((layer) => {
    let layerLegend: Layer['legend']
    try {
      layerLegend = layer.legend // LegendHandler is not defined
    } catch {
      return []
    }
    // A layer can expose a single legend or, since karonte 0.16, a list of them
    const legends = Array.isArray(layerLegend) ? layerLegend : layerLegend ? [layerLegend] : []
    return legends.flatMap((legend) => {
      if (!legend.items || !legend.items.length) return []
      return {
        label: localizedText(legend.title),
        type: 'Feature',
        legendData: legend.items.map((i) => {
          return {
            label: localizedText(i.text),
            symbol: {
              type: 'circle',
              fill: {
                color: 'color' in i ? i.color : undefined
              }
            }
          }
        })
      }
    })
  })
}

async function print() {
  printing.value = true
  const logores = await fetch('/app/printimg.txt')
  const logo = await logores.text()
  const language = window.navigator.language
  const timeZone = Intl.DateTimeFormat(language, { timeZone: 'UTC' }).resolvedOptions().timeZone

  const body = {
    data: {
      client: 'WildfireSAFE',
      description: description.value,
      filename: {
        opts: {
          dateformat: 'yyyyMMdd_Hmmssa'
        },
        value: 'wfsafe_export_{timestamp}'
      },
      legend: buildLegend(),
      excludeLegend: false,
      timezone: timeZone,
      logo: 'data:image/png;base64,' + logo,
      title: nameField.value
    },
    image: (await widgetBuilderRef.value?.view?.takeScreenshot('png', 95))?.dataUrl
  }
  try {
    const res = await fetch('https://report.technosylva.com/generate/generic/print', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })
    const filename = await res.text()
    const url = `https://report.technosylva.com/files/${filename}.pdf`
    let found
    for (let index = 0; index < 20; index++) {
      try {
        await new Promise((resolve) => setTimeout(resolve, 5000))
        await fetch(url)
        found = true
        break
      } catch {
        // file not yet generated; keep polling
      }
    }
    if (!found) throw new Error('error generating PDF')
    const a = document.createElement('a')
    a.href = url
    a.target = '_blank'
    a.download = 'printmap.pdf'
    a.click()
  } catch (error) {
    console.error('PDF print failed', error)
  }
  printing.value = false
}
</script>

<style scoped>
.container-print {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: rgb(var(--v-theme-surface));
}
.action-buttons {
  box-shadow: 0px -1px 0px rgb(var(--v-theme-surface));
  background-color: rgb(var(--v-theme-surface-light));
}
.cancel-button {
  background-color: rgb(var(--v-theme-surface));
}
</style>
